import { MaterialReactTable } from "material-react-table";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { scheduleData } from "./dummyData";
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TimelineComponent from "./TimelineComponent";
import { useParams } from "react-router-dom";
import ScheduleComponent from "./ScheduleComponent";
import SubTaskModal from "./SubTasksModal";
import ResourcesModal from "../../Resources/ResourcesModal";
import { useResources } from "../../Resources/useResources";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CreateTaskDialog from "./CreateTaskDialog";
// import { getEquip4d5d } from '../../PMSTab/4d5d/api'
// import Project4d5d from '../../PMSTab/4d5d'
import {
  getSchedule,
  getTrackItem,
  updateSchedule,
  update4D,
  getProjectData,
  getPlantData,
  create4DTask,
} from "./api";
import { tableWithBorderedCells } from "../../../../../../styles/muiProps";
import TrackItemDialog from "./TrackItemDialog";
// import { AdjustedData } from "./dummyData";
// import RowEditableTable from "../../../../../../components/Tables/RowEditableTable";
import { AuthContext } from "../../../../../../context/authContext";
import { useSchedule4D } from "../../MCS3/Schedule4D/Schedule4DContext";
import { formatMrqNumbersFromTask } from "../../MCS3/Schedule4D/scheduleTaskApi";
import {
  aggregateSiStatus,
  normalizeSi,
  patchTaskSiInList,
} from "../../MCS3/Schedule4D/siUtils";
import {
  getBaseTaskCode,
  parseTaskCode,
} from "../../../../Project/plant/PlantTaskPage/TaskList/components/taskCodeUtils";

const normalizeDescription = (str) => {
  if (str == null || str === "") return "";
  return String(str)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ");
};

/** Extract code prefix (first two segments) from code, e.g. "2.1.1" → "2.1" */
const getCodePrefix = (code) => {
  if (!code || typeof code !== "string") return null;
  const parts = String(code).trim().split(".").filter(Boolean);
  if (parts.length < 2) return parts[0] || null;
  return parts.slice(0, 2).join(".");
};

/** Check if code has exactly 2 segments (e.g. "1.1", "2.1") = main task */
const isMainTaskCode = (code) => {
  if (!code || typeof code !== "string") return false;
  const parts = String(code).trim().split(".").filter(Boolean);
  return parts.length === 2;
};

/** Subtask rows only (e.g. 2.2.1-group), excluding main 2.2-group for group aggregates */
const isSubtaskRow = (row) => {
  const { code } = parseTaskCode(row?.taskCode || "");
  const parts = String(code).trim().split(".").filter(Boolean);
  return parts.length > 2;
};

const Project4d = ({ plant = false, projectFilter = [] }) => {
  const { id: projectIdFromRoute, plantId } = useParams();
  const { user, accessToken } = useContext(AuthContext);
  const schedule4D = useSchedule4D();
  const [rows, setRows] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [trackItemDialog, setTrackItemDialog] = useState(dialogInitalState);
  const [stats, setStats] = useState({
    totalEstimatedTime: 0,
    siCompleteCount: 0,
    siTotalCount: 0,
  });
  const [fourDFilter, setFourDFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [subTaskModal, setSubTaskModal] = useState({
    open: false,
    mainTask: null,
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);

  const { resources, invalidate: invalidateResources } = useResources();

  const { totalEstimatedTime, siCompleteCount, siTotalCount } = stats;

  /** Map: groupName → code prefix (from existing tasks). When group selected, prefix is auto-set. */
  const groupNameToPrefix = useMemo(() => {
    const taskCodes = (rows || []).map((r) => r.taskCode).filter(Boolean);
    const map = new Map();
    taskCodes.forEach((tc) => {
      const { code, groupName: gn } = parseTaskCode(tc);
      if (gn && code) {
        const p = getCodePrefix(code);
        if (p && !map.has(gn)) map.set(gn, p);
      }
    });
    return map;
  }, [rows]);

  const normalizeUidForSearch = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const uidPart = s.includes("::") ? s.split("::").pop() : s;
    return uidPart.trim().toLowerCase();
  };

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const result =
      fourDFilter === "all"
        ? rows
        : rows.filter((row) => row.fourD === fourDFilter);

    const searchTerm = (search || "").trim().toLowerCase();
    const searched =
      searchTerm.length > 0
        ? result.filter((row) => {
            const task = (row.task || "").toLowerCase();
            const code = (row.taskCode || "").toLowerCase();
            const group = (row.groupName || "").toLowerCase();
            const uid = normalizeUidForSearch(row.uid);
            const members = Array.isArray(row.members)
              ? row.members.join(" ").toLowerCase()
              : "";
            return (
              task.includes(searchTerm) ||
              code.includes(searchTerm) ||
              group.includes(searchTerm) ||
              uid.includes(searchTerm) ||
              members.includes(searchTerm)
            );
          })
        : result;

    // Group rows by group name (part after "-" in taskCode)
    const groupMap = new Map();
    searched.forEach((row) => {
      const { groupName: gn } = parseTaskCode(row.taskCode || "");
      if (!gn) return;
      if (!groupMap.has(gn)) groupMap.set(gn, []);
      groupMap.get(gn).push(row);
    });

    const groupRows = [];
    groupMap.forEach((tasks, gn) => {
      // Include the parent "main" row (2-segment code, e.g. 2.1-group) when it is
      // missing from the filtered set (e.g. 4D filter shows only MCS subtasks).
      const seenIds = new Set(tasks.map((t) => t.id));
      const augmented = [...tasks];
      (rows || []).forEach((row) => {
        const { code, groupName } = parseTaskCode(row.taskCode || "");
        if (groupName !== gn || seenIds.has(row.id)) return;
        if (isMainTaskCode(code)) {
          augmented.push(row);
          seenIds.add(row.id);
        }
      });

      const subtasks = augmented.filter(isSubtaskRow);
      const dateSource = subtasks.length > 0 ? subtasks : augmented;

      const siStatus = aggregateSiStatus(subtasks.length > 0 ? subtasks : augmented);

      const starts = dateSource
        .map((t) => t.plannedStart)
        .filter(Boolean)
        .sort();
      const ends = dateSource
        .map((t) => t.plannedEnd)
        .filter(Boolean)
        .sort();
      const revisedStarts = dateSource
        .map((t) => t.revisedStart)
        .filter(Boolean)
        .sort();
      const revisedEnds = dateSource
        .map((t) => t.revisedEnd)
        .filter(Boolean)
        .sort();
      const actualStarts = dateSource
        .map((t) => t.actualStart)
        .filter(Boolean)
        .sort();
      const actualEnds = dateSource
        .map((t) => t.actualEnd)
        .filter(Boolean)
        .sort();

      const totalBudgeted = subtasks.reduce((sum, t) => {
        if (t.budgetedHrDollar == null || t.budgetedHrDollar === "") return sum;
        const v = Number(t.budgetedHrDollar);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);

      const totalActual = subtasks.reduce((sum, t) => {
        if (t.actualHrDollar == null || t.actualHrDollar === "") return sum;
        const v = Number(t.actualHrDollar);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);

      const prefix = groupNameToPrefix.get(gn) || "";

      const mainTaskRow = augmented.find((t) => {
        const { code, groupName } = parseTaskCode(t.taskCode || "");
        return groupName === gn && isMainTaskCode(code);
      });

      // Group UID only from the group/main task (e.g. 9.4-testG::V -101), never subtasks.
      const groupUid = mainTaskRow
        ? String(mainTaskRow.uid ?? "").trim() ||
          parseTaskCode(mainTaskRow.taskCode || "").uid ||
          ""
        : "";

      const { code: mainWbsCode } = parseTaskCode(mainTaskRow?.taskCode || "");
      const wbsCode = mainWbsCode || prefix || "";

      const uniqueFourD = [
        ...new Set(augmented.map((t) => t.fourD).filter(Boolean)),
      ];
      const fourDLabel = uniqueFourD.join(", ");

      groupRows.push({
        id: `group-${gn}`,
        task: gn,
        type: null,
        taskCode: mainTaskRow?.taskCode || `${wbsCode}-${gn}`,
        uid: groupUid,
        fourD: fourDLabel,
        wbsId: wbsCode || prefix,
        mrqNumber: "",
        si: subtasks.every((t) => normalizeSi(t.si) === 1) ? 1 : 0,
        _siStatus: siStatus,
        plannedStart: starts[0] || "",
        plannedEnd: ends[ends.length - 1] || "",
        revisedStart: revisedStarts[0] || "",
        revisedEnd: revisedEnds[revisedEnds.length - 1] || "",
        actualStart: actualStarts[0] || "",
        actualEnd: actualEnds[actualEnds.length - 1] || "",
        budgetedHrDollar: totalBudgeted,
        actualHrDollar: totalActual,
        _isGroup: true,
        _groupName: gn,
      });
    });

    groupRows.sort((a, b) => {
      const dateA = a.plannedStart ? new Date(a.plannedStart) : new Date(0);
      const dateB = b.plannedStart ? new Date(b.plannedStart) : new Date(0);
      return dateA - dateB;
    });

    return groupRows;
  }, [rows, fourDFilter, groupNameToPrefix, search]);

  const onSave = async () => {
    updateSchedule({ scheduleTasks: rows });
  };

  const onTrackItem = async (row) => {
    console.log(row);
    const result = await getTrackItem(row.original.id).then((res) =>
      setTrackItemDialog((prev) => ({
        ...prev,
        open: true,
        item: row.original,
        data: res,
      })),
    );
  };

  const onClose = async () => {
    setTrackItemDialog(dialogInitalState);
  };

  const formatTasksFor4D = (tasks) => {
    if (!Array.isArray(tasks)) return [];

    const normalizeDate = (value) => {
      if (!value) return null;
      return value;
    };

    return (
      tasks
        // Ignore archived tasks coming from the API
        .filter((taskItem) => !taskItem.isArchieved)
        .map((taskItem) => {
          const estimatedTime =
            taskItem.estimatedTime != null
              ? Number(taskItem.estimatedTime)
              : taskItem.manhours != null
                ? Number(taskItem.manhours)
                : 0;

          const hours = taskItem.hours != null ? Number(taskItem.hours) : null;

          return {
            id: taskItem.id,
            taskCode: taskItem.taskCode || "",
            name: taskItem.name || null,
            task: taskItem.task || null,
            type: (taskItem.type || "").toLowerCase(),
            si: normalizeSi(taskItem.si),
            startDate: normalizeDate(taskItem.startDate),
            endDate: normalizeDate(taskItem.endDate || taskItem.targetDate),
            revisedStart: normalizeDate(
              taskItem.revisedStart || taskItem.revisedStartDate,
            ),
            revisedEnd: normalizeDate(
              taskItem.revisedEnd || taskItem.revisedEndDate,
            ),
            actualStart: normalizeDate(taskItem.actualStart),
            actualEnd: normalizeDate(taskItem.actualEnd),
            estimatedTime,
            manhours:
              taskItem.manhours != null ? Number(taskItem.manhours) : null,
            hours,
            mrqMaterials: taskItem.mrqMaterials,
            mrvMaterials: taskItem.mrvMaterials,
            uid:
              parseTaskCode(taskItem.taskCode || "").uid ||
              String(taskItem.uid ?? "").trim(),
          };
        })
        // Only include tasks with taskCode
        .filter((task) => task.taskCode)
    );
  };

  const buildRowsFromTasks = (tasks /*, options = {} */) => {
    // const { procurementProgressFromContext } = options;
    if (!Array.isArray(tasks) || tasks.length === 0) {
      setRows([]);
      setAllTasks([]);
      setStats({
        totalEstimatedTime: 0,
        siCompleteCount: 0,
        siTotalCount: 0,
      });
      return;
    }

    // Format tasks first to ensure consistent structure (and filter archived)
    const formattedTasks = formatTasksFor4D(tasks);

    if (formattedTasks.length === 0) {
      setRows([]);
      setAllTasks([]);
      setStats({
        totalEstimatedTime: 0,
        siCompleteCount: 0,
        siTotalCount: 0,
      });
      return;
    }

    // Calculate stats (total estimated manhours and SI completion)
    let totalEst = 0;
    let siComplete = 0;
    let siCount = 0;

    formattedTasks.forEach((task) => {
      const est = parseFloat(task.estimatedTime ?? task.manhours);
      if (!isNaN(est)) {
        totalEst += est;
      }

      if (!isSubtaskRow(task)) return;
      siCount += 1;
      if (normalizeSi(task.si) === 1) siComplete += 1;
    });

    setStats({
      totalEstimatedTime: totalEst,
      siCompleteCount: siComplete,
      siTotalCount: siCount,
    });

    setAllTasks(formattedTasks);

    // Normalize ISO dates to YYYY-MM-DD so <input type="date"> can display them
    const toDateStr = (v) => {
      if (!v) return "";
      return typeof v === "string" ? v.slice(0, 10) : "";
    };

    // One row per task (no grouping), with manual WBS from taskCode and 4D from type
    const rowsForTable = formattedTasks.map((task) => {
      const type = (task.type || "").toLowerCase();

      let fourD = "";
      if (type === "engineering") fourD = "EMS";
      else if (type === "procurement") fourD = "PMS";
      else if (type === "fabrication") fourD = "CMS";
      else if (type === "itp") fourD = "ITP";

      const { code: displayCode } = parseTaskCode(task.taskCode || "");
      const mrqNumber = formatMrqNumbersFromTask(task);
      const plannedEnd = toDateStr(task.endDate);
      return {
        id: task.id,
        uid: task.uid || "",
        task: task.task || task.name || "",
        type: task.type || null,
        taskCode: task.taskCode,
        fourD,
        wbsId: displayCode || task.taskCode || "",
        mrqNumber,
        si: normalizeSi(task.si),
        plannedStart: toDateStr(task.startDate),
        plannedEnd,
        revisedStart: toDateStr(task.revisedStart),
        revisedEnd: toDateStr(task.revisedEnd),
        actualStart: toDateStr(task.actualStart),
        actualEnd: toDateStr(task.actualEnd),
        budgetedHrDollar: task.estimatedTime || 0,
        actualHrDollar: task.manhours ?? 0,
        resourceId: task.resourceId ?? task.resource?.id ?? null,
        resource: task.resource ?? null,
      };
    });

    const sortedData = rowsForTable.sort((a, b) => {
      const dateA = a.plannedStart ? new Date(a.plannedStart) : new Date(0);
      const dateB = b.plannedStart ? new Date(b.plannedStart) : new Date(0);
      return dateA - dateB;
    });

    setRows(sortedData);
  };

  const arrange4dData = useCallback(async () => {
    console.log("plantsssssssss two", plant);

    // Normal project mode - get all tasks for the project
    if (!plant) {
      if (!projectIdFromRoute) {
        setRows([]);
        setAllTasks([]);
        return;
      }
      try {
        const tasks = await getProjectData(projectIdFromRoute, accessToken);
        console.log("4D tasks (single project)", tasks);
        const tasksArray = Array.isArray(tasks) ? tasks : [];
        buildRowsFromTasks(tasksArray);
      } catch (e) {
        console.error("Error loading 4D data for project:", e);
        setRows([]);
        setAllTasks([]);
      }
      return;
    }

    // Plant mode - get all tasks for the plant
    if (!plantId) {
      setRows([]);
      setAllTasks([]);
      return;
    }
    console.log("asdfsafdds");

    try {
      const tasks = await getPlantData(plantId, accessToken);
      let tasksArray = Array.isArray(tasks) ? tasks : [];

      if (
        schedule4D?.schedulePlantId === plantId &&
        Array.isArray(schedule4D?.scheduleMaterials) &&
        schedule4D.scheduleMaterials.length > 0
      ) {
        const byScheduleTaskId = new Map(
          schedule4D.scheduleMaterials.map((row) => [
            row.id ?? row.scheduleTaskId,
            row,
          ]),
        );
        tasksArray = tasksArray.map((task) => {
          const sch = byScheduleTaskId.get(task.id);
          if (!sch) return task;
          return {
            ...task,
            mrqMaterials: sch.mrqMaterials ?? task.mrqMaterials,
            mrvMaterials: sch.mrvMaterials ?? task.mrvMaterials,
          };
        });
      }

      const actualStartMap = schedule4D?.procurementActualStartMap ?? new Map();
      const actualEndMap = schedule4D?.procurementActualEndMap ?? new Map();
      const budgetedCostMap =
        schedule4D?.procurementBudgetedCostMap ?? new Map();
      const actualCostMap = schedule4D?.procurementActualCostMap ?? new Map();

      if (
        schedule4D?.schedulePlantId === plantId &&
        (actualStartMap.size > 0 ||
          actualEndMap.size > 0 ||
          budgetedCostMap.size > 0 ||
          actualCostMap.size > 0)
      ) {
        tasksArray = tasksArray.map((task) => {
          const type = (task?.type || "").toLowerCase();
          if (type !== "procurement") return task;
          const taskCode = normalizeDescription(task?.taskCode ?? "");
          const baseTaskCode = normalizeDescription(
            getBaseTaskCode(task?.taskCode ?? ""),
          );
          const taskDesc = normalizeDescription(task?.task ?? task?.name ?? "");
          const actualStart =
            (baseTaskCode && actualStartMap.get(baseTaskCode)) ??
            (taskCode && actualStartMap.get(taskCode)) ??
            (taskDesc && actualStartMap.get(taskDesc)) ??
            task?.actualStart ??
            null;
          const actualEnd =
            (baseTaskCode && actualEndMap.get(baseTaskCode)) ??
            (taskCode && actualEndMap.get(taskCode)) ??
            (taskDesc && actualEndMap.get(taskDesc)) ??
            task?.actualEnd ??
            null;
          const budgetedCost =
            (baseTaskCode && budgetedCostMap.get(baseTaskCode)) ??
            (taskCode && budgetedCostMap.get(taskCode)) ??
            (taskDesc && budgetedCostMap.get(taskDesc)) ??
            null;
          const actualCost =
            (baseTaskCode && actualCostMap.get(baseTaskCode)) ??
            (taskCode && actualCostMap.get(taskCode)) ??
            (taskDesc && actualCostMap.get(taskDesc)) ??
            null;
          return {
            ...task,
            actualStart: actualStart || task?.actualStart,
            actualEnd: actualEnd || task?.actualEnd,
            estimatedTime: budgetedCost ?? task?.estimatedTime,
            manhours: actualCost ?? task?.manhours,
          };
        });
      }

      buildRowsFromTasks(tasksArray);
    } catch (e) {
      console.error("Error arranging 4D data for plant:", e);
      setRows([]);
      setAllTasks([]);
    }
  }, [
    accessToken,
    plant,
    plantId,
    projectIdFromRoute,
    schedule4D?.procurementActualStartMap,
    schedule4D?.procurementActualEndMap,
    schedule4D?.procurementBudgetedCostMap,
    schedule4D?.procurementActualCostMap,
    schedule4D?.schedulePlantId,
    schedule4D?.scheduleMaterials,
  ]);

  useEffect(() => {
    arrange4dData();
  }, [arrange4dData]);

  const onRowClick = (row) => {
    setSubTaskModal({ open: true, mainTask: row.original });
  };

  const handleTaskSiUpdated = useCallback((taskId, si) => {
    setAllTasks((prev) => {
      const next = patchTaskSiInList(prev, taskId, si);
      let siComplete = 0;
      let siCount = 0;
      next.forEach((task) => {
        if (!isSubtaskRow(task)) return;
        siCount += 1;
        if (normalizeSi(task.si) === 1) siComplete += 1;
      });
      setStats((s) => ({
        ...s,
        siCompleteCount: siComplete,
        siTotalCount: siCount,
      }));
      return next;
    });
    setRows((prev) => patchTaskSiInList(prev, taskId, si));
  }, []);

  return (
    <Box p={2}>
      <Stack
        sx={{ paddingBottom: "2rem" }}
        direction={"row"}
        justifyContent="space-between"
        height={"3rem"}
        alignItems="center"
      >
        <h2>Schedule</h2>
        <Button
          variant="contained"
          size="small"
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Schedule
        </Button>
      </Stack>
      {!plant && (
        <Box
          sx={{
            display: "flex",
            gap: 4,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle1">
            Total Manhour: <strong>{totalEstimatedTime.toFixed(2)}</strong>
          </Typography>
          <Typography variant="subtitle1">
            SI Complete:{" "}
            <strong>
              {siTotalCount > 0
                ? `${siCompleteCount}/${siTotalCount}`
                : "—"}
            </strong>
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            SI Complete:{" "}
            <strong>
              {siTotalCount > 0
                ? `${siCompleteCount}/${siTotalCount}`
                : "—"}
            </strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">4D Filter:</Typography>
            <ToggleButtonGroup
              size="small"
              value={fourDFilter}
              exclusive
              onChange={(_, value) => {
                if (value !== null) setFourDFilter(value);
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="EMS">EMS</ToggleButton>
              <ToggleButton value="PMS">PMS</ToggleButton>
              <ToggleButton value="CMS">CMS</ToggleButton>
              <ToggleButton value="ITP">ITP</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Search task, code, group, UID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<ManageAccountsIcon />}
              onClick={() => setResourcesModalOpen(true)}
            >
              Resources
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <ScheduleComponent
          rows={filteredRows}
          setRows={setRows}
          projectId={projectIdFromRoute}
          onRowClick={onRowClick}
          accessToken={accessToken}
          onRefresh={arrange4dData}
          resources={resources}
        />
      </Box>
      <SubTaskModal
        open={subTaskModal.open}
        onClose={() => setSubTaskModal({ open: false, mainTask: null })}
        mainTask={subTaskModal.mainTask}
        allTasks={allTasks}
        accessToken={accessToken}
        onRefresh={arrange4dData}
        resources={resources}
        onSiUpdated={handleTaskSiUpdated}
      />
      <ResourcesModal
        open={resourcesModalOpen}
        onClose={() => setResourcesModalOpen(false)}
        onResourcesChange={invalidateResources}
      />
      {/* <TrackItemDialog
        open={trackItemDialog.open}
        onClose={onClose}
        item={trackItemDialog.item}
        data={trackItemDialog.data}
      /> */}
      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        tasks={allTasks}
        plant={plant}
        plantId={plantId}
        projectId={projectIdFromRoute}
        accessToken={accessToken}
        user={user}
        resources={resources}
        title="Create 4D Task"
        onSubmit={async (payload) => {
          await create4DTask({
            task: payload,
            projectId: plant ? null : projectIdFromRoute,
            plantId: plant ? plantId : null,
            accessToken,
          });
        }}
        onCreated={arrange4dData}
      />
    </Box>
  );
};
export default Project4d;

const dialogInitalState = {
  open: false,
  taskGroupName: null,
};
